import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';

import { gqlClient } from '@/api';

/**
 * Whether this server can talk to App Store Connect.
 *
 * The Overview and the header both used to state this as a fact written into
 * the source — `app store connect · not connected`, true on the day it was
 * written and true only by accident afterwards. It is now an answer from the
 * server that holds the credentials, which is the only thing that knows.
 *
 * `connected` is about configuration, not about whether Apple would answer.
 * Proving the second needs a request, and a header that made one on every
 * navigation would spend the studio's rate limit on rendering a breadcrumb.
 */
/**
 * What came of the last attempt to fetch a report.
 *
 * Null means nobody has ever asked — which must not be drawn as "Apple has
 * nothing for us". Those are the two states most easily confused on this
 * screen, and their fixes are opposite: one is "press the button", the other is
 * "go and look at the key".
 */
export interface AppStoreSyncRun {
  /** The day asked for, `YYYY-MM-DD`. */
  reportDate: string;
  /**
   * `report` when Apple sent one, `noSales` for a settled day with nothing on
   * it, and otherwise the name of what went wrong — `notReady`,
   * `unauthorised`, `forbidden`, `rateLimited`, `badRequest`, `unexpected`,
   * `unreachable`, `unreadable`, `unsignable`, `unstorable`.
   */
  outcome: string;
  /** Rows stored. Zero alongside `report` is a real answer: quiet days exist. */
  rowCount: number;
  /** What Apple said, when it did not send a report. Shown as-is. */
  detail: string | null;
  ranAt: string;
}

export interface AppStoreStatus {
  connected: boolean;
  /**
   * The key tokens are signed with — the identifier, never the key.
   *
   * Shown so somebody can tell at a glance whether the server holds the key
   * they think it does, which is the question a rotation leaves behind.
   */
  keyId: string | null;
  /** Where the credentials in use come from — lower-cased from the enum. */
  source: 'settings' | 'environment' | 'none';
  lastSync: AppStoreSyncRun | null;
}

const APPSTORE_STATUS = gql`
  query AppStoreStatus {
    appStoreStatus {
      connected
      keyId
      source
      lastSync {
        reportDate
        outcome
        rowCount
        detail
        ranAt
      }
    }
  }
`;

export const useAppStoreStatus = () =>
  useQuery({
    queryKey: ['appStoreStatus'],
    queryFn: async () => {
      const { appStoreStatus } = await gqlClient.request<{ appStoreStatus: AppStoreStatus }>(
        APPSTORE_STATUS,
      );
      return {
        ...appStoreStatus,
        source: appStoreStatus.source.toLowerCase() as AppStoreStatus['source'],
      };
    },
    /*
     * Configuration changes when somebody edits an environment file and
     * restarts the server, which no amount of polling from a browser will
     * observe sooner. This asks once per five minutes per mount rather than on
     * every navigation — the shell reads it on every screen in the admin.
     *
     * Refetch-on-focus is off globally in `main.tsx`, not here; this is only
     * about remounting. Said explicitly because the comment used to credit
     * `staleTime` with both, which would leave somebody turning the global
     * setting back on believing this line still covered it.
     *
     * One retry, not the library's three. The failure this screen names in its
     * own words — "the session may have expired" — is permanent, and three
     * retries with backoff spend four requests and about seven seconds getting
     * to an answer that was known at the first. One covers a genuine blip and
     * costs a second.
     */
    staleTime: 5 * 60_000,
    retry: 1,
  });

const SYNC_APPSTORE = gql`
  mutation SyncAppStore {
    syncAppStore {
      reportDate
      outcome
      rowCount
      detail
      ranAt
    }
  }
`;

/**
 * Ask Apple now, rather than waiting for the server's timer.
 *
 * The only way to find out whether a key actually works. Everything the setup
 * guide cannot check — the role, a revoked key, a vendor number belonging to
 * another account — looks identical from this screen until a request is made,
 * so this is as much for the first minute after configuring as for a studio in
 * a hurry.
 */
export const useSyncAppStore = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await gqlClient.request<{ syncAppStore: AppStoreSyncRun }>(SYNC_APPSTORE)).syncAppStore,
    /*
     * Both queries, and the promise is **returned** rather than discarded.
     *
     * Two bugs lived in the one line this replaces. It invalidated the status
     * and not the overview, so a sync that fetched new days from Apple updated
     * the "last asked" line and left every figure on the screen as it was —
     * pressing the button appeared to do nothing, which is exactly what it was
     * reported as. And `void` let the mutation settle the moment the server
     * answered, so the button came back to "Sync now" while the figures behind
     * it were still being refetched. Returning the promise keeps `isPending`
     * true until the screen the reader is looking at has actually caught up.
     */
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['appStoreStatus'] }),
        queryClient.invalidateQueries({ queryKey: ['appStoreOverview'] }),
      ]),
  });
};

/**
 * Money as the server sends it: an amount **and** the currency it is in.
 *
 * A list rather than one number, everywhere it appears. Apple pays per
 * contract, so one period can carry several currencies — and adding them makes
 * a figure in no currency at all, which looks exactly like a total.
 */
export interface AppStoreMoney {
  currency: string;
  /**
   * A decimal string, summed exactly by Postgres.
   *
   * It reaches the screen as a string and is turned into a number only at the
   * last step, to be formatted — never added to anything, which is the part the
   * `numeric` column exists for.
   */
  amount: string;
}

export interface AppStoreOverview {
  /** Earliest and latest day held, or null when nothing has been stored. */
  from: string | null;
  to: string | null;
  /** How many distinct days are held — **not** the span between the two. */
  dayCount: number;
  /** First purchases. Updates and re-downloads are deliberately not counted. */
  downloads: number;
  /**
   * The same over the period before this one, or null when it was not fetched.
   *
   * Null and zero say different things and the screen prints them differently:
   * zero is "nothing sold then", null is "nobody asked then".
   */
  previousDownloads: number | null;
  proceeds: AppStoreMoney[];
  byDay: Array<{ reportDate: string; downloads: number; proceeds: AppStoreMoney[] }>;
  byApp: Array<{
    sku: string;
    title: string;
    /** Apple's id for the row, and the website app carrying it once linked. */
    appleId: string | null;
    linkedApp: { id: string; name: string } | null;
    downloads: number;
    proceeds: AppStoreMoney[];
    previousDownloads: number | null;
    /** That app's trial conversion as a percentage, or null if no trial began. */
    trialConversion: number | null;
  }>;
  byMarket: Array<{ countryCode: string; downloads: number }>;
  /**
   * The same storefronts ranked by money.
   *
   * A separate list, not a column: the market that downloads most is often not
   * the one that pays most, which is why the design puts the two side by side.
   */
  byMarketProceeds: Array<{ countryCode: string; proceeds: AppStoreMoney[] }>;
  /**
   * Paid subscriptions active on the newest day held, or null if never fetched.
   *
   * The newest day's, never the period's sum: a subscription count is a
   * snapshot, and adding thirty days of the same thousand subscribers reads as
   * thirty thousand.
   */
  activeSubscriptions: number | null;
  activeSubscriptionsOn: string | null;
  /**
   * Trials that became paid over trials that started, as a percentage.
   *
   * ⚠️ Not a cohort rate — a trial begun in June converts in July, so this
   * divides one period's conversions by a different period's starts. A studio
   * that doubles its trials watches it fall while nothing has got worse, which
   * is why the screen prints the two counts beside it.
   */
  trialConversion: number | null;
  trialsStarted: number | null;
  trialsConverted: number | null;
}

/**
 * The periods the Overview offers, as the design names them.
 *
 * `90d` stands where the design has `12m`: the backfill reaches ninety days, so
 * a twelve-month tab would show three months under a year's label — the same
 * class of untruth as the sample figures, dressed as a control.
 */
export const RANGES = [
  ['7d', '7d'],
  ['mtd', 'MTD'],
  ['30d', '30d'],
  ['90d', '90d'],
] as const;

const APPSTORE_OVERVIEW = gql`
  query AppStoreOverview($range: String!) {
    appStoreOverview(range: $range) {
      from
      to
      dayCount
      downloads
      previousDownloads
      proceeds {
        currency
        amount
      }
      byDay {
        reportDate
        downloads
        proceeds {
          currency
          amount
        }
      }
      byApp {
        sku
        title
        appleId
        linkedApp {
          id
          name
        }
        downloads
        previousDownloads
        trialConversion
        proceeds {
          currency
          amount
        }
      }
      byMarket {
        countryCode
        downloads
      }
      activeSubscriptions
      activeSubscriptionsOn
      trialConversion
      trialsStarted
      trialsConverted
      byMarketProceeds {
        countryCode
        proceeds {
          currency
          amount
        }
      }
    }
  }
`;

/**
 * The figures, for a studio that has some.
 *
 * `enabled` on purpose: five aggregate queries are not worth running for a
 * screen that is about to draw a setup guide, and the Overview knows which of
 * those it is before this is needed.
 */
export const useAppStoreOverview = (enabled: boolean, range: string) =>
  useQuery({
    // The range is in the key, so switching tabs fetches rather than showing
    // one period's figures under another period's heading.
    queryKey: ['appStoreOverview', range],
    queryFn: async () =>
      (
        await gqlClient.request<{ appStoreOverview: AppStoreOverview }>(APPSTORE_OVERVIEW, {
          range,
        })
      ).appStoreOverview,
    enabled,
    staleTime: 60_000,
  });

/* ─────────────────── the Connections card ─────────────────── */

/**
 * What the admin may know about the stored credentials. No field carries
 * the private key; `privateKeyStored` is the whole of it.
 */
export interface AppStoreConnection {
  issuerId: string;
  keyId: string;
  vendorNumber: string;
  privateKeyStored: boolean;
  source: 'settings' | 'environment' | 'none';
  environmentConfigured: boolean;
  storageUnavailable: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface AppStoreConnectionInput {
  issuerId: string;
  keyId: string;
  vendorNumber: string;
  /** The whole `.p8` as text, or absent to keep the stored one. */
  privateKeyPem?: string;
}

export interface AppStoreConnectionTest {
  ok: boolean;
  message: string;
}

const CONNECTION_FIELDS = `
  issuerId keyId vendorNumber privateKeyStored source environmentConfigured storageUnavailable updatedAt updatedByName
`;
const ADMIN_APPSTORE_CONNECTION = gql`query AdminAppStoreConnection { adminAppStoreConnection { ${CONNECTION_FIELDS} } }`;
const UPDATE_APPSTORE_CONNECTION = gql`
  mutation UpdateAppStoreConnection($input: AppStoreConnectionInput!) {
    updateAppStoreConnection(input: $input) { ${CONNECTION_FIELDS} }
  }
`;
const REMOVE_APPSTORE_CONNECTION = gql`
  mutation RemoveAppStoreConnection { removeAppStoreConnection { ${CONNECTION_FIELDS} } }
`;
const TEST_APPSTORE_CONNECTION = gql`
  mutation TestAppStoreConnection {
    testAppStoreConnection {
      ok
      message
    }
  }
`;

const fromServer = (raw: AppStoreConnection): AppStoreConnection => ({
  ...raw,
  source: raw.source.toLowerCase() as AppStoreConnection['source'],
});

export const useAppStoreConnection = () =>
  useQuery({
    queryKey: ['adminAppStoreConnection'],
    queryFn: async () =>
      fromServer(
        (
          await gqlClient.request<{ adminAppStoreConnection: AppStoreConnection }>(
            ADMIN_APPSTORE_CONNECTION,
          )
        ).adminAppStoreConnection,
      ),
  });

/** Writes are followed by a status refetch: the shell's dot and the Overview read `appStoreStatus`. */
const settle = (queryClient: ReturnType<typeof useQueryClient>, saved: AppStoreConnection) => {
  queryClient.setQueryData(['adminAppStoreConnection'], saved);
  void queryClient.invalidateQueries({ queryKey: ['appStoreStatus'] });
};

export const useSaveAppStoreConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AppStoreConnectionInput) =>
      fromServer(
        (
          await gqlClient.request<{ updateAppStoreConnection: AppStoreConnection }>(
            UPDATE_APPSTORE_CONNECTION,
            { input },
          )
        ).updateAppStoreConnection,
      ),
    onSuccess: (saved) => settle(queryClient, saved),
  });
};

export const useRemoveAppStoreConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      fromServer(
        (
          await gqlClient.request<{ removeAppStoreConnection: AppStoreConnection }>(
            REMOVE_APPSTORE_CONNECTION,
          )
        ).removeAppStoreConnection,
      ),
    onSuccess: (saved) => settle(queryClient, saved),
  });
};

/** One report request to Apple, from the server. Not cached, not retried: the server throttles it. */
export const useTestAppStoreConnection = () =>
  useMutation({
    mutationFn: async () =>
      (
        await gqlClient.request<{ testAppStoreConnection: AppStoreConnectionTest }>(
          TEST_APPSTORE_CONNECTION,
        )
      ).testAppStoreConnection,
  });

/** One app as App Store Connect lists it, for the picker. */
export interface AppStoreApp {
  appleId: string;
  name: string;
  bundleId: string;
  sku: string;
}

const APPSTORE_APPS = gql`
  query AppStoreApps {
    appStoreApps {
      apps {
        appleId
        name
        bundleId
        sku
      }
      problem
    }
  }
`;

/**
 * The studio's apps on App Store Connect, fetched when the picker opens and
 * not before: every call leaves the building and the server throttles it.
 * `problem` is the sentence to show in place of a list — no key, a key with
 * the Sales role, a rate limit — and the fallback is the same every time:
 * type the Apple ID.
 */
export const useAppStoreApps = (enabled: boolean) =>
  useQuery({
    queryKey: ['appStoreApps'],
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async () =>
      (
        await gqlClient.request<{ appStoreApps: { apps: AppStoreApp[]; problem: string | null } }>(
          APPSTORE_APPS,
        )
      ).appStoreApps,
  });
